// src/components/AIChatWidget.jsx
// Drop into App.js: <AIChatWidget />
// Floats bottom-right. Visitor can ask anything about Albin.
/* eslint-disable max-lines */

import React, { useState, useRef, useEffect } from 'react'
import { useAI } from '../hooks/useAI'

const WELCOME =
  "Hi! I'm Albin's AI assistant. Ask me anything about his experience, skills, or work."

const styles = {
  fab: {
    position: 'fixed',
    bottom: '1.5rem',
    right: '1.5rem',
    zIndex: 300,
    width: 52,
    height: 52,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent-teal), #38bdf8)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(99,211,183,0.45)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    color: '#0d0d14'
  },
  panel: {
    position: 'fixed',
    bottom: '5rem',
    right: '1.5rem',
    zIndex: 300,
    width: 'min(380px, calc(100vw - 2rem))',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-raised)',
    border: '1px solid var(--border-medium)',
    borderRadius: 16,
    boxShadow: 'var(--shadow-card)',
    overflow: 'hidden'
  },
  header: {
    padding: '14px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--bg-overlay)'
  },
  headerIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'linear-gradient(135deg, var(--accent-teal), #38bdf8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: '#0d0d14',
    fontFamily: 'var(--font-display)'
  },
  headerTitle: { fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  headerSubtitle: { fontSize: '0.7rem', color: 'var(--accent-teal)', margin: 0 },
  msgList: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  typing: {
    alignSelf: 'flex-start',
    padding: '9px 13px',
    borderRadius: '14px 14px 14px 4px',
    background: 'var(--bg-glass)',
    border: '1px solid var(--border-subtle)',
    display: 'flex',
    gap: 4,
    alignItems: 'center'
  },
  inputBar: {
    padding: '10px 12px',
    borderTop: '1px solid var(--border-subtle)',
    display: 'flex',
    gap: 8,
    background: 'var(--bg-overlay)'
  },
  textarea: {
    flex: 1,
    resize: 'none',
    background: 'var(--bg-glass)',
    border: '1px solid var(--border-medium)',
    borderRadius: 10,
    padding: '8px 12px',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    fontSize: '0.85rem',
    outline: 'none',
    lineHeight: 1.5
  }
}

const msgStyle = role => ({
  alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  maxWidth: '85%',
  padding: '9px 13px',
  borderRadius: role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
  background: role === 'user' ? 'var(--accent-teal)' : 'var(--bg-glass)',
  border: role === 'user' ? 'none' : '1px solid var(--border-subtle)',
  color: role === 'user' ? '#0d0d14' : 'var(--text-primary)',
  fontSize: '0.82rem',
  lineHeight: 1.5,
  fontFamily: 'var(--font-body)'
})

const dotStyle = i => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--accent-teal)',
  display: 'inline-block',
  animation: `pulse-dot 1.2s ease-in-out ${i * 0.2}s infinite`
})

const sendBtnStyle = active => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  background: active ? 'var(--accent-teal)' : 'var(--bg-glass)',
  border: '1px solid var(--border-medium)',
  cursor: active ? 'pointer' : 'not-allowed',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: active ? '#0d0d14' : 'var(--text-muted)',
  transition: 'background 0.2s ease',
  alignSelf: 'flex-end'
})

const pushEvent = (event, data = {}) => {
  if (window.dataLayer) window.dataLayer.push({ event, ...data })
}

export default function AIChatWidget() {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME }])
  const [loading, setLoading] = useState(false)
  const [cooldown, setCooldown] = useState(false)
  const cooldownTimer = useRef(null)
  const bottomRef = useRef(null)
  const { chat } = useAI()

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, open])

  useEffect(() => {
    return () => clearTimeout(cooldownTimer.current)
  }, [])

  const send = async () => {
    const text = input.trim()
    if (!text || loading || cooldown) return
    const history = messages.filter(m => m.role !== 'system')
    setMessages(prev => [...prev, { role: 'user', content: text }])
    setInput('')
    setLoading(true)
    try {
      const data = await chat(text, history)
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }])
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }
      ])
    } finally {
      setLoading(false)
      setCooldown(true)
      cooldownTimer.current = setTimeout(() => setCooldown(false), 1500)
    }
  }

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const handleToggle = () => {
    const next = !open
    setOpen(next)
    if (next) pushEvent('ai_chat_opened')
  }

  const active = !!input.trim() && !loading && !cooldown

  return (
    <>
      <button
        onClick={handleToggle}
        aria-label={open ? 'Close AI chat' : 'Open AI chat'}
        style={styles.fab}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1)'
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)'
        }}
      >
        {open ? (
          <svg width='18' height='18' viewBox='0 0 18 18' fill='none'>
            <path
              d='M2 2l14 14M16 2L2 16'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
            />
          </svg>
        ) : (
          <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
            <path
              d='M12 2C6.48 2 2 6.03 2 11c0 2.61 1.19 4.97 3.07 6.63L4 21l3.64-1.55A10.52 10.52 0 0012 20c5.52 0 10-4.03 10-9S17.52 2 12 2z'
              fill='currentColor'
            />
          </svg>
        )}
      </button>

      {open && (
        <div role='dialog' aria-label='AI chat with Albin' style={styles.panel}>
          <div style={styles.header}>
            <div style={styles.headerIcon}>AI</div>
            <div>
              <p style={styles.headerTitle}>Ask about Albin</p>
              <p style={styles.headerSubtitle}>● Powered by Cloudflare AI · Chats not stored</p>
            </div>
          </div>

          <div style={styles.msgList}>
            {messages.map((msg, i) => (
              <div key={i} style={msgStyle(msg.role)}>
                {msg.content}
              </div>
            ))}
            {loading && (
              <div style={styles.typing}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={dotStyle(i)} />
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div style={styles.inputBar}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder='Ask a question…'
              rows={1}
              maxLength={500}
              aria-label='Chat input'
              style={styles.textarea}
            />
            <button
              onClick={send}
              disabled={!active}
              aria-label='Send message'
              style={sendBtnStyle(active)}
            >
              <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                <path
                  d='M1 7h12M8 3l4 4-4 4'
                  stroke='currentColor'
                  strokeWidth='1.8'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
